/**
 * What the health banner shows for one `/health` answer (R92-M7): the
 * platform answers 503 whenever its overall status is degraded — including
 * when only the project backups cannot be restored, which affects starting a
 * stopped project and nothing else — and 503 `shutting_down` while a deploy
 * replaces the process, which is not an outage.
 */
export type HealthStatus = 'ok' | 'degraded' | 'backups' | 'unreachable'

/** The coarse per-component map `/health` carries. */
export interface HealthBody {
  status?: string
  components?: Record<string, string>
}

/** Rows whose degradation affects one thing (starting from a backup), not the platform. */
const BACKUPS_ONLY = new Set(['backups'])

/**
 * The banner state for an answer, or `null` when the answer says nothing
 * about the platform's health (rate limited, or a deploy in progress) and the
 * previous state stands.
 *
 * @param res - The response's `ok` and status code.
 * @param body - The parsed body, when it parsed.
 * @returns The state, or null.
 */
export function healthStatusFrom(
  res: { ok: boolean; status: number },
  body: HealthBody | null | undefined,
): HealthStatus | null {
  if (res.status === 429) return null
  if (body?.status === 'shutting_down') return null
  const degraded = !res.ok || body?.status === 'degraded'
  if (!degraded) return 'ok'
  const rows = Object.entries(body?.components ?? {}).filter(([, v]) => v !== 'operational')
  if (rows.length > 0 && rows.every(([key]) => BACKUPS_ONLY.has(key))) return 'backups'
  return 'degraded'
}
